'use client';

import { createContext, useContext } from 'react';

const NumberFormatContext = createContext<(value: number) => string>(String);

/**
 * Sets how library components print counts a person reads: Counter (so Badge
 * and Tab counts), the DataTable footer, page picker and bulk bar, the
 * Accordion.Trigger count, QuantityStepper, FieldCharacterCount, the
 * AvatarGroup overflow, PhotoUploader, booking slot counts and the dropdown
 * result announcements. Without a provider numbers print through `String`, unchanged. Pass a
 * stable function so consumers do not re-render on every parent render.
 *
 * @example
 * ```tsx
 * const groupThousands = new Intl.NumberFormat('en-US').format;
 *
 * <NumberFormatProvider value={groupThousands}>
 *   <DataTable
 *     columns={columns}
 *     data={orders}
 *     rowCount={3866}
 *     page={page}
 *     onPageChange={setPage}
 *     itemLabel="order"
 *   />
 * </NumberFormatProvider>
 * ```
 */
export const NumberFormatProvider = NumberFormatContext.Provider;

/**
 * Returns the formatter set by the nearest {@link NumberFormatProvider}, or
 * `String` when none is mounted.
 *
 * @example
 * ```tsx
 * const formatNumber = useFormatNumber();
 * return <span>{formatNumber(rowCount)}</span>;
 * ```
 */
export const useFormatNumber = () => useContext(NumberFormatContext);
